export type DenefitsView = 'dashboard' | 'listing' | 'business' | 'business-details';
export type DenefitsDetailMode = 'all' | 'contract';

export interface DenefitsCategoryOption {
  id: number;
  key: string;
  label: string;
  tone: 'blue' | 'green' | 'amber' | 'violet' | 'rose' | 'slate';
  partner?: boolean;
  supportsOrp?: boolean;
  supportsPaymentType?: boolean;
  supportsChallenge?: boolean;
  supportsContractView?: boolean;
}

export interface DenefitsStatusOption {
  value: string;
  label: string;
}

export interface DenefitsBalanceSnapshot {
  available_balance: number;
  upcoming_balance: number;
  total_amount_to_pay: number;
  urgent_balance: number;
  total_urgent_payments: number;
  available_balance_by_source: {
    card: number;
    bank_account: number;
  };
}

export interface DenefitsFundingOption {
  label: string;
  value: 'card' | 'bank_account';
  amount: number;
}

export interface DenefitsDashboardRow {
  unpaid_filter_id: number;
  category: string;
  label: string;
  tone: DenefitsCategoryOption['tone'];
  businesses: number;
  unpaidPayments: number;
  urgentCount: number;
  amount: number;
}

export interface DenefitsDashboardSection {
  key: string;
  title: string;
  rows: DenefitsDashboardRow[];
}

export interface DenefitsCategoryDisplay {
  icon: string;
  iconBgClass: string;
  iconClass: string;
}

export const PARTNER_FILTER_IDS = [9, 10, 11] as const;
export const LENDER_REDIRECT_FILTER_ID = 12;
export const CONTRACT_STATUS_PILL_FILTER_IDS = [1, 2, 5, 6, 7, 9, 10] as const;

export function showsContractStatusPill(filterId: number): boolean {
  return (CONTRACT_STATUS_PILL_FILTER_IDS as readonly number[]).includes(filterId);
}

export const DENEFITS_CATEGORIES: DenefitsCategoryOption[] = [
  { id: 1, key: 'ACH_BUSINESS', label: 'ACH Businesses', tone: 'blue', supportsContractView: true },
  { id: 2, key: 'PROTECTED', label: 'Protected', tone: 'green', supportsOrp: true, supportsContractView: true },
  { id: 3, key: 'ACH_PP_AR_SUB', label: 'ACH PP/AR/PP Subscription', tone: 'violet', supportsPaymentType: true, supportsContractView: true },
  { id: 4, key: 'BUSINESS_REWARDS', label: 'Business Rewards', tone: 'amber', supportsChallenge: true },
  { id: 5, key: 'WON_DISPUTES', label: 'Won Disputes', tone: 'green', supportsContractView: true },
  { id: 6, key: 'CASH_ADJUSTMENTS', label: 'Cash Adjustments', tone: 'slate', supportsContractView: true },
  { id: 7, key: 'BUSINESS', label: 'Businesses', tone: 'amber', supportsContractView: true },
  { id: 8, key: 'IN_HOUSE', label: 'In House Financing', tone: 'rose', supportsContractView: true },
  { id: 9, key: 'PARTNER', label: 'Partner', tone: 'violet', partner: true, supportsContractView: true },
  { id: 10, key: 'ACH_PARTNER', label: 'ACH Partner', tone: 'green', partner: true, supportsContractView: true },
  { id: 11, key: 'PARTNER_CASH_ADJUSTMENTS', label: 'Partner Cash Adjustments', tone: 'slate', partner: true, supportsContractView: true },
  { id: 12, key: 'LENDEE_PAYMENTS', label: 'Lendee Payments', tone: 'blue' },
];

export const EMPTY_DENEFITS_BALANCE: DenefitsBalanceSnapshot = {
  available_balance: 0,
  upcoming_balance: 0,
  total_amount_to_pay: 0,
  urgent_balance: 0,
  total_urgent_payments: 0,
  available_balance_by_source: {
    card: 0,
    bank_account: 0,
  },
};

export function getDenefitsCategoryById(filterId: number): DenefitsCategoryOption | undefined {
  return DENEFITS_CATEGORIES.find((option) => option.id === filterId);
}

export function getDenefitsCategoryByKey(key: string): DenefitsCategoryOption | undefined {
  return DENEFITS_CATEGORIES.find((option) => option.key === key);
}

export function getDenefitsCategoryKey(filterId: number): string {
  return getDenefitsCategoryById(filterId)?.key ?? 'ACH_BUSINESS';
}

export function getDenefitsCategoryLabel(filterId: number): string {
  return getDenefitsCategoryById(filterId)?.label ?? 'ACH Businesses';
}

export function isPartnerFilter(filterId: number): boolean {
  return PARTNER_FILTER_IDS.includes(filterId as (typeof PARTNER_FILTER_IDS)[number]);
}

export function getDenefitsStatusOptions(filterId: number): DenefitsStatusOption[] {
  if (filterId === 9 || filterId === 10 || filterId === 11) {
    return [
      { value: '0', label: 'Both' },
      { value: '1', label: 'Active Partners' },
      { value: '2', label: 'Inactive Partners' },
    ];
  }

  return [
    { value: '0', label: 'Both' },
    { value: '1', label: 'Active Businesses' },
    { value: '2', label: 'Inactive Businesses' },
  ];
}

export function getVersionLabel(versionType: number): string {
  if (versionType === 1) return 'V1';
  if (versionType === 2) return 'V2';
  return 'ALL';
}

const DENEFITS_CATEGORY_DISPLAY: Record<string, DenefitsCategoryDisplay> = {
  ACH_BUSINESS: { icon: 'building2', iconBgClass: 'bg-[#eef5ff]', iconClass: 'text-[#0c66e8]' },
  PROTECTED: { icon: 'shield', iconBgClass: 'bg-[#edfff3]', iconClass: 'text-[#15b268]' },
  ACH_PP_AR_SUB: { icon: 'credit-card', iconBgClass: 'bg-[#f4e5ff]', iconClass: 'text-[#ad46ff]' },
  BUSINESS_REWARDS: { icon: 'gift', iconBgClass: 'bg-[#fff0f7]', iconClass: 'text-[#e91e63]' },
  WON_DISPUTES: { icon: 'circle-check-big', iconBgClass: 'bg-[#edfff3]', iconClass: 'text-[#15b268]' },
  CASH_ADJUSTMENTS: { icon: 'dollar-sign', iconBgClass: 'bg-[#fff9e8]', iconClass: 'text-[#ffa514]' },
  BUSINESS: { icon: 'briefcaseBusiness', iconBgClass: 'bg-[#fff9e8]', iconClass: 'text-[#ffa514]' },
  IN_HOUSE: { icon: 'user', iconBgClass: 'bg-[#fff0f0]', iconClass: 'text-[#f05050]' },
  PARTNER: { icon: 'users', iconBgClass: 'bg-[#f4e5ff]', iconClass: 'text-[#ad46ff]' },
  ACH_PARTNER: { icon: 'users-round', iconBgClass: 'bg-[#edfff3]', iconClass: 'text-[#15b268]' },
  PARTNER_CASH_ADJUSTMENTS: { icon: 'circle-dollar-sign', iconBgClass: 'bg-[#fff9e8]', iconClass: 'text-[#ffa514]' },
  LENDEE_PAYMENTS: { icon: 'receipt', iconBgClass: 'bg-[#eef5ff]', iconClass: 'text-[#0c66e8]' },
};

const DEFAULT_DENEFITS_CATEGORY_DISPLAY: DenefitsCategoryDisplay = {
  icon: 'banknote',
  iconBgClass: 'bg-gray-50',
  iconClass: 'text-gray-600',
};

export function getDenefitsCategoryDisplay(categoryKey: string): DenefitsCategoryDisplay {
  return DENEFITS_CATEGORY_DISPLAY[categoryKey] ?? DEFAULT_DENEFITS_CATEGORY_DISPLAY;
}

export function normalizeToneClasses(tone: DenefitsCategoryOption['tone']): { chip: string; badge: string } {
  switch (tone) {
    case 'green':
      return {
        chip: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        badge: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200',
      };
    case 'amber':
      return {
        chip: 'border-amber-200 bg-amber-50 text-amber-700',
        badge: 'bg-amber-500/10 text-amber-700 dark:text-amber-200',
      };
    case 'violet':
      return {
        chip: 'border-violet-200 bg-violet-50 text-violet-700',
        badge: 'bg-violet-500/10 text-violet-700 dark:text-violet-200',
      };
    case 'rose':
      return {
        chip: 'border-rose-200 bg-rose-50 text-rose-700',
        badge: 'bg-rose-500/10 text-rose-700 dark:text-rose-200',
      };
    case 'slate':
      return {
        chip: 'border-slate-200 bg-slate-50 text-slate-700',
        badge: 'bg-slate-500/10 text-slate-700 dark:text-slate-200',
      };
    case 'blue':
    default:
      return {
        chip: 'border-blue-200 bg-blue-50 text-blue-700',
        badge: 'bg-blue-500/10 text-blue-700 dark:text-blue-200',
      };
  }
}

export function todayInConfiguredTimezone(): string {
  const configuredTimezone =
    localStorage.getItem('timezoneName') ||
    localStorage.getItem('timezone') ||
    'America/Los_Angeles';

  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: configuredTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function getVersionDateRange(versionType: number): { startDate: string; endDate: string } {
  const today = todayInConfiguredTimezone();
  if (versionType === 1) {
    return { startDate: '2017-01-01', endDate: '2025-06-30' };
  }
  if (versionType === 2) {
    return { startDate: '2025-07-01', endDate: today };
  }
  return { startDate: '2017-01-01', endDate: today };
}
